import { AtlasObjectType, IAtlasObjectRef } from '@common/helpers/atlasUrls'
import {
  IAtlasObjectPreview,
  atlasObjectRefKey,
  isResolvedPreview,
} from '@common/interfaces/atlasObjects'
import { ITask } from '@common/interfaces/task'
import { IActionWithParams } from '../apiMiddleware'
import { UPDATE_TASK_SUCCESS } from '../myTasks/actions'
import * as actions from './actions'

export interface IAtlasLinksState {
  /** Keyed `Type:id` — ids are only unique within their own table. */
  byKey: Record<string, IAtlasObjectPreview>
  requestedKeys: Record<string, true>
  /**
   * Keys whose request has already failed once. What bounds the retry: a key is
   * released for one more attempt the first time it fails and left marked after
   * that, so a transient blip heals itself and a persistent failure stops.
   */
  failedKeys: Record<string, true>
}

const initialState: IAtlasLinksState = { byKey: {}, requestedKeys: {}, failedKeys: {} }

/**
 * Flat cache of the records behind rendered Atlas links.
 *
 * Not createLoadingDataReducer: there is no single "current" payload to render
 * and no loading state to show — a chip renders the raw URL until its entry
 * lands, which is also where it stays for an id that never resolves.
 *
 * `requestedKeys` is what stops a re-request per render: every rendered chip asks
 * for its own ref, and a timeline can hold many. A failed key is released once so
 * the batch is not stranded, and `failedKeys` bounds that to a single retry.
 */
const atlasLinks = (
  state: IAtlasLinksState = initialState,
  action: IActionWithParams<{ refs?: IAtlasObjectRef[] }>,
): IAtlasLinksState => {
  switch (action.type) {
    case actions.LIST_OBJECT_PREVIEWS_REQUEST: {
      const refs = action.params?.refs ?? []
      if (!refs.length) {
        return state
      }
      return {
        ...state,
        requestedKeys: refs.reduce(
          (acc, ref) => ({ ...acc, [atlasObjectRefKey(ref)]: true as const }),
          state.requestedKeys,
        ),
      }
    }

    case actions.LIST_OBJECT_PREVIEWS_SUCCESS: {
      const previews = (action.data as { data?: IAtlasObjectPreview[] } | undefined)?.data ?? []
      if (!previews.length) {
        return state
      }
      return {
        ...state,
        byKey: previews.reduce(
          (acc, preview) => ({ ...acc, [atlasObjectRefKey(preview)]: preview }),
          state.byKey,
        ),
      }
    }

    /**
     * Release each key once, so a request that failed can be tried again.
     *
     * REQUEST marks every submitted ref and the hook only dispatches for refs
     * absent from both maps, so without any release a single failed request
     * stranded that whole batch as raw URLs until a page reload.
     *
     * Releasing unconditionally is worse: the hook recomputes the key as missing
     * the moment it disappears and schedules another request on a zero-delay
     * timer, so an endpoint that keeps failing is re-hit for as long as the
     * message stays on screen. Hence `failedKeys` — one retry, then the key stays
     * marked.
     *
     * Returning `state` itself on a repeat failure is load-bearing, not a
     * micro-optimisation: a new object here re-renders every mounted chip, which
     * is the first step of that same loop.
     */
    case actions.LIST_OBJECT_PREVIEWS_FAILURE: {
      const refs = action.params?.refs ?? []
      if (!refs.length) {
        return state
      }

      const requestedKeys = { ...state.requestedKeys }
      const failedKeys = { ...state.failedKeys }
      let released = false

      refs.forEach((ref) => {
        const key = atlasObjectRefKey(ref)
        if (failedKeys[key]) {
          return
        }
        delete requestedKeys[key]
        failedKeys[key] = true
        released = true
      })

      return released ? { ...state, requestedKeys, failedKeys } : state
    }

    /**
     * Keep a cached chip honest when the task is edited anywhere in the app — a
     * rename or a completion changes both its label and its status glyph. Came
     * across with the cache when task previews stopped having one of their own.
     */
    case UPDATE_TASK_SUCCESS: {
      const updated = action.data as (ITask & { success?: boolean }) | undefined
      if (!updated?.id) {
        return state
      }

      const key = atlasObjectRefKey({ type: AtlasObjectType.Task, id: updated.id })
      const cached = state.byKey[key]
      if (!cached || !isResolvedPreview(cached) || !cached.task) {
        return state
      }

      return {
        ...state,
        byKey: {
          ...state.byKey,
          [key]: {
            ...cached,
            name: updated.title ?? cached.name,
            task: {
              ...cached.task,
              title: updated.title ?? cached.task.title,
              status: updated.status ?? cached.task.status,
            },
          },
        },
      }
    }

    default:
      return state
  }
}

export default atlasLinks
